import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, inject, Input, Output, ViewChild } from '@angular/core';
import { NumberFormatInfo } from '@sb/library/data-models';
import { LocalizationDecimalPipe, LocalizationService } from '@sb/library/localization';
import { debounceTime } from 'rxjs';
import { FormFieldInputBaseComponent } from '../form-field-input-base.component';
import { ValidationLimits, Validators } from '@sb/library/base';

@Component({
    selector: 'lib-input-number',
    templateUrl: './form-field-number.component.html',
    styleUrls: ['./form-field-number.component.scss']
})
export class FormFieldNumberComponent extends FormFieldInputBaseComponent<number | null> implements AfterViewInit {
    @ViewChild('numberinput') numberInput!: ElementRef;

    private _decimalFormatter!: LocalizationDecimalPipe;
    protected _validationRegex = new RegExp(/[0-9]/, 'g');
    private lastValid = '';

    protected _LocalizationService!: LocalizationService;
    protected _NumberFormatInfo!: NumberFormatInfo;

    minDecimalPlaces: number = 0;
    maxDecimalPlaces: number = 0;

    @Input() applyFormatting: boolean = true;
    @Input() allowNegativeInput: boolean = false;

    @Output() valueChanged: EventEmitter<void> = new EventEmitter();

    private _groupSeparatorRegex!: RegExp;

    constructor() {
        super();

        this._decimalFormatter = new LocalizationDecimalPipe();
        this._LocalizationService = inject(LocalizationService);
        this._NumberFormatInfo = this._LocalizationService.NumberFormatInfo;

        this._groupSeparatorRegex = new RegExp(`[${this._NumberFormatInfo.GroupSeparator}]`, 'g');
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.formControl.AddDefaultValidators(Validators.max(ValidationLimits.Numbers.Max));
    }

    ngAfterViewInit() {
        if (this.debounceTime !== null && this.debounceTime !== undefined) {
            this.viewControl.valueChanges.pipe(debounceTime(this.debounceTime)).subscribe(value => {
                this.realTimeValueChanged.emit(this.GetFormControlValue(value));
            })
        }
    }

    override OnBlur(): void {
        const numberValue = this.GetFormControlValue(this.viewControl.value);
        this.formControl.setValue(numberValue);
        this.change.emit(numberValue);

        super.OnBlur();
    }

    private GetFormControlValue(value: string): number | null {
        if (value !== null && value.trim() !== '') {
            value = value.replace(this._NumberFormatInfo.GroupSeparator!, '')
                         .replace(this._NumberFormatInfo.DecimalSeparator!, '.');

            let numberValue: number = Number(value);
            if (!isNaN(numberValue)) {
                if (numberValue > ValidationLimits.Numbers.DisplayMax) {
                    return ValidationLimits.Numbers.DisplayMax;
                }
                return numberValue;
            }
        }

        return null;
    }

    ValueFormatter(numberValue: number | null): string {
        return this._decimalFormatter.transform(numberValue, this.minDecimalPlaces, this.maxDecimalPlaces);
    }

    OnFocus(): void {
        this.onInput();
    }

    override writeValue(numberValue: number | null): void {
        let formattedValue: string;
        if (numberValue === null) {
            formattedValue = '';
        } else if (this.applyFormatting) {
            formattedValue = this.ValueFormatter(numberValue);
        } else {
            formattedValue = String(numberValue);
        }

        this.viewControl.setValue(formattedValue, { emitViewToModelChange: false });
        this.lastValid = formattedValue;
    }

    override ObservePristine(): void {
        // This is done below in the host listener instead
    }

    SetFocus(): void {
        if (this.numberInput) {
            this.numberInput.nativeElement.focus();
        }
    }

    @HostListener('input', ['$event'])
    onInput() {
        // Firefox 1.0+
        //var isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

        let eventValue = this.viewControl.value;
        if (!eventValue) {
            if (this.lastValid != '') {
                this.valueChanged.emit();
                this.formControl.isDirty = true;
            }

            this.lastValid = '';
        } else {
            eventValue = eventValue.replace(this._groupSeparatorRegex, '');
            // on input, run regex to only allow certain characters and format
            const validValue = (eventValue.match(this._validationRegex) || []).join('');

            if (validValue) {
                if (this.lastValid != validValue) {
                    this.valueChanged.emit();
                    this.formControl.isDirty = true;
                }

                this.lastValid = validValue;
                this.viewControl.setValue(validValue);
            } else {
                this.viewControl.setValue(this.lastValid);
            }
        }
    }
}
